import { ITimesheet } from '@jobcost-monorepo/crud/dto/timesheet';
import { Icon } from '@jobcost-monorepo/ui';
import {
  cn,
  floatToHours,
  ITimesheetsByResourceByDate,
  useBreakpoints,
} from '@jobcost-monorepo/utils';
import dayjs from '@jobcost-monorepo/dayjs-config';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface IProps {
  setFormElement: React.Dispatch<React.SetStateAction<ITimesheet | undefined>>;
  setFormOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleDelete: (element: ITimesheet) => void;
  dataList: ITimesheetsByResourceByDate[];
  startDate?: string;
  endDate?: string;
  isLocked?: boolean;
}

const TimesheetSingleBody = React.forwardRef<HTMLDivElement, IProps>(
  (
    {
      setFormElement,
      setFormOpen,
      handleDelete,
      dataList,
      startDate,
      endDate,
      isLocked,
    },
    ref,
  ) => {
    if (
      dataList.length === 0 ||
      startDate === undefined ||
      endDate === undefined
    )
      return <div />;
    const { t } = useTranslation('timesheets');
    const data = dataList[0];
    const start = dayjs.utc(startDate);
    const end = dayjs.utc(endDate);
    const daysInMonth = end.diff(start, 'day') + 1;
    const { isSmall } = useBreakpoints();

    const daysArray = Array.from({ length: daysInMonth }, (_, i) =>
      start.add(i, 'day'),
    );

    const getTimesheetsForDate = (date: string): ITimesheet[] => {
      const group = data.groupedByDate.find(
        (g) => dayjs.utc(g.operationDate).format('YYYY-MM-DD') === date,
      );
      return group ? group.timesheets : [];
    };

    const getTotalHoursByMarkType = (markType: string): string => {
      const totalHours = data.groupedByDate.reduce((total, group) => {
        return (
          total +
          group.timesheets.reduce((sum, timesheet) => {
            return (
              sum +
              (timesheet.markType === markType ? timesheet.workedHours ?? 0 : 0)
            );
          }, 0)
        );
      }, 0);

      return floatToHours(totalHours);
    };

    let markTypes = Array.from(
      new Set(
        data.groupedByDate.flatMap((group) =>
          group.timesheets.map((ts) => ts.markType),
        ),
      ),
    );

    // Sposta "WorkedHours" all'inizio dell'array se presente
    if (markTypes.includes('WorkedHours')) {
      markTypes = [
        'WorkedHours',
        ...markTypes.filter((markType) => markType !== 'WorkedHours'),
      ];
    }

    const handleEdit = (element: ITimesheet) => {
      setFormElement(element);
      setFormOpen(true);
    };

    const getActivityLabel = (timesheetElement: ITimesheet): string => {
      const workedHours = floatToHours(timesheetElement.workedHours ?? 0);
      const activity =
        timesheetElement.job?.name &&
        timesheetElement.markType === 'WorkedHours'
          ? `${timesheetElement.job?.name} - ${timesheetElement.order?.name}`
          : t(`enum.markType.${timesheetElement.markType}`);

      return `${workedHours} - [${activity}]`;
    };

    return (
      <div
        ref={ref}
        className="rounded-lg border-purple70 dark:border-grey border-[1px] size-full overflow-hidden overflow-y-auto"
      >
        <div className="flex flex-row sticky top-0 font-semibold border-b-[1px] border-purple70 dark:border-grey bg-white dark:bg-dark">
          <div className="w-20 px-2 py-2 text-left">
            {t('grid.header.date')}
          </div>
          <div className="px-2 py-2 text-left">{data.resource.name}</div>
        </div>

        <table className="w-full border-0">
          <tbody className="overflow-hidden">
            {daysArray.map((day, index) => {
              const dateKey = day.format('YYYY-MM-DD');
              const timesheets = getTimesheetsForDate(dateKey);

              return (
                <tr
                  key={dateKey}
                  className={
                    index % 2
                      ? 'bg-violet2 dark:bg-transparent'
                      : 'bg-violet5 dark:bg-darkGrey'
                  }
                >
                  <td className="p-2 w-20">
                    {day.locale('it').format('DD ddd').toUpperCase()}
                  </td>
                  <td className="p-2">
                    {timesheets.length > 0 ? (
                      timesheets.map((timesheet, index) => (
                        <div
                          key={`${dateKey}-${timesheet.id}`}
                          className={cn(
                            'flex overflow-hidden',
                            isSmall && 'max-w-64',
                          )}
                        >
                          <div
                            key={index}
                            className="flex-col flex-1 overflow-hidden whitespace-nowrap"
                          >
                            <div className="text-ellipsis overflow-hidden whitespace-nowrap">
                              {getActivityLabel(timesheet)}
                            </div>
                            <div className="text-ellipsis overflow-hidden whitespace-nowrap">
                              {timesheet.note}
                            </div>
                          </div>
                          {!isLocked && (
                            <div className="flex print:hidden no-print">
                              <div
                                className="mr-2 cursor-pointer"
                                onClick={() => handleEdit(timesheet)}
                              >
                                <Icon
                                  iconName="edit"
                                  className="hover:opacity-50 hover:cursor-pointer fill-black stroke-black dark:fill-white dark:stroke-white"
                                />
                              </div>
                              <div
                                className="cursor-pointer"
                                onClick={() => handleDelete(timesheet)}
                              >
                                <Icon
                                  iconName="remove"
                                  className="fill-pink stroke-pink hover:opacity-50 hover:cursor-pointer"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div></div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div
          className={cn(
            'border-t-[1px] border-purple70 dark:border-grey sticky bottom-0 bg-white dark:bg-dark',
            isSmall ? 'pl-0' : 'pl-20',
          )}
        >
          <div className="flex px-2 py-2 font-bold">
            {markTypes.map((markType) => (
              <div key={markType} className="flex-col mr-8">
                <div>{t(`enum.markType.${markType}`)}</div>
                <div className="flex w-full justify-end">
                  {getTotalHoursByMarkType(markType)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  },
);

TimesheetSingleBody.displayName = 'TimesheetSingleBody';

export default TimesheetSingleBody;
